import { inject, Injectable } from '@angular/core';
import { IOrder, IOrderDataUpdate } from '../typing/order.interface';
import { ILocation, IShortLocation } from '../typing/location.interface';
import { OrderStatusEnum } from '../enums/status.enum';
import { OrdersService } from './orders.service';
import { Observable } from 'rxjs';
import { Order } from '../classes/order.class';
import { Person } from '../classes/person.class';
import { TimeZoneService } from './time-zone.service';

@Injectable({
    providedIn: 'root'
})

export class AssignOrdersService {
    private efficiency = 1; //Коэффициент эффективности работников
    balanceWorkload = false;
    assignAllTasks = false;
    assignAllTasksByDuration = false;
    private workersEndTimeLastTask = new Map<number, Date>(); //Время окончания работ в предидущей заявке
    protected orderService = inject(OrdersService);
    protected timeZoneService = inject(TimeZoneService);

    /**
     * Вычисление расстояния между двумя точками с известными координатами (Haversine formula)
     * @param location1
     * @param location2
     */
    calculateDistance(location1: ILocation | IShortLocation, location2: ILocation): number {
        const R = 6371; // Radius of the Earth in kilometers

        const dLat = this.toRadians(location2.latitude - location1.latitude);
        const dLng = this.toRadians(location2.longitude - location1.longitude);
        const lat1 = this.toRadians(location1.latitude);
        const lat2 = this.toRadians(location2.latitude);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c; // Distance in kilometers
    }

    toRadians(deg: number): number {
        return (deg * Math.PI) / 180;
    }

    isNotFullTimeSlot(task: Order, workerOrders: Order[], balanceNumber: number): boolean {
        // Создаем Map для хранения заявок по временным слотам
        const timeSlotMap = new Map<string, Order[]>();

        // Заполняем Map заявками работника
        for (const order of workerOrders) {
            const key = `${order.data.dateFrom}-${order.data.dateTo}`;
            if (!timeSlotMap.has(key)) {
                timeSlotMap.set(key, []);
            }

            timeSlotMap.get(key)!.push(order);
        }

        // Ключ для текущей задачи
        const taskKey = `${task.data.dateFrom}-${task.data.dateTo}`;

        if (this.assignAllTasksByDuration) {
            const ordersInSameTimeSlot = timeSlotMap.get(taskKey);
            const totalDurationInSlot = ordersInSameTimeSlot?.reduce((sum: number, task: Order) => sum + this.calculateTaskDuration(task), 0) || 0;
            return totalDurationInSlot + this.calculateTaskDuration(task) <= balanceNumber;
        }
        // Получаем количество заявок в том же временном слоте
        const ordersNumberInSameTimeSlot = timeSlotMap.get(taskKey)?.length || 0;

        return ordersNumberInSameTimeSlot < balanceNumber;
    }

    canAssignTask(worker: Person, task: Order, workerOrdersMap: Map<number, Order[]>, balanceNumber?: number): boolean {
        const workerStartTime = worker.getScheduleTimeDate(task.data.dateFrom, 'startWorkTime');
        const workerEndTime = worker.getScheduleTimeDate(task.data.dateFrom, 'endWorkTime');

        if (!workerStartTime || !workerEndTime ) {
            return false;
        }
        const workerOrders = workerOrdersMap.get(worker.data.id) || [];

        //Если хотим распределить все заявки без учета времени
        if ((this.assignAllTasks || this.assignAllTasksByDuration) && balanceNumber && workerOrders.length) {
            return this.isNotFullTimeSlot(task, workerOrders, balanceNumber);
        }

        // Если хотим распределить заявки равномерно с учетом времени
        if (this.balanceWorkload && balanceNumber && workerOrders.length
            && !this.isNotFullTimeSlot(task, workerOrders, balanceNumber)) {
            return false;
        }

        // Если задач нет, проверяем возможность прибытия и завершения первой задачи
        if (workerOrders?.length === 0) {
            const { startTime, endTime } = this.getNextTaskTiming(workerStartTime, task, worker.data.group.location);
            if (startTime >= workerStartTime && endTime <= workerEndTime && endTime <= task.data.dateTo) {
                this.workersEndTimeLastTask.delete(worker.data.id);
                this.workersEndTimeLastTask.set(worker.data.id, endTime);
                return true;
            }

            return false;
        }

        //Проверка наличия в таймслоте у работника времени на эту заявку
        if (!this.calculateWorkerLoad(workerOrders, task)) {
            return false;
        }

        // Для последующих задач учитываем завершение предыдущей задачи
        const lastTask = workerOrders[workerOrders.length - 1];
        lastTask.data.endTime = lastTask?.data.endTime || this.calculateTaskEndTime(workerOrders, task);
        const { startTime, endTime } = this.getNextTaskTiming(
            lastTask?.data.endTime,
            task,
            lastTask.data.location
        );

        if (startTime >= lastTask?.data.endTime && endTime <= workerEndTime && endTime <= task.data.dateTo) {
            this.workersEndTimeLastTask.delete(worker.data.id);
            this.workersEndTimeLastTask.set(worker.data.id, endTime);
            return true;
        }

        return false;
    }

    assignTasksToWorkers(
        tasks: Order[], workers: Person[], workerOrdersMap: Map<number, Order[]>,
        efficiency: number, balanceWorkload: boolean, assignAllTasks: boolean, balanceByDuration?: boolean): Observable<IOrder[]> {
        this.efficiency = efficiency;
        this.balanceWorkload = balanceWorkload; //распределить равномерно с учетом времени
        this.assignAllTasks = assignAllTasks; //распределить все без исключения заявки равномерно по кол-ву
        this.assignAllTasksByDuration = !!balanceByDuration; // распределить все без исключения заявки равномерно по общей продолжительности работы

        // Группируем задачи по временным слотам и локациям
        const groupedMaps: Map<string, Map<string, Order[]>> = this.groupTasksByLocation(tasks);
        let slotIndex = 0;
        for (const [timeSlot, group] of groupedMaps.entries()) {
            const totalTasksInTimeSlot = Array.from(group.values()).reduce((sum, tasks) => sum + tasks.length, 0);
            const totalDurationInSlot = Array.from(group.values()).flat().reduce((sum, task) => sum + this.calculateTaskDuration(task), 0);
            const baseTaskNumber = Math.floor(totalTasksInTimeSlot / workers.length);
            const baseDuration = totalDurationInSlot / workers.length;
            const extraTasks = totalTasksInTimeSlot % workers.length;
            const workerTaskLimits = new Map<number, number>();
            const averageTaskDuration = Math.floor(totalDurationInSlot / totalTasksInTimeSlot);
            let balanceNumber: number | undefined = undefined;

            //Для нивелирования не кратности работников к заявкам, добавляем 1 некоторым работникам
            workers.forEach((worker, index) => {
                workerTaskLimits.set(worker.data.id, baseTaskNumber + (index < extraTasks ? 1 : 0));
            });
            //Цикл одного временного периода
            for (const groupedTasks of group.values()) {
                //Цикл в заявках одного адреса
                for (const task of groupedTasks) {
                    let bestWorker: Person | null = null;
                    let minTravelTime = Infinity;

                    // Приоритизация работников с последней заявкой в той же локации
                    const workersInSameLocation = workers.filter(worker => {
                        const workerOrders = workerOrdersMap.get(worker.data.id) || [];
                        const lastOrder = workerOrders[workerOrders.length - 1];
                        return lastOrder && this.isSameLocation(lastOrder.data.location, task.data.location);
                    });

                    // Сначала проверяем работников в той же локации
                    for (const worker of workersInSameLocation) {
                        if (balanceWorkload || assignAllTasks) {balanceNumber = workerTaskLimits?.get(worker.data.id);}
                        if (balanceByDuration) { balanceNumber = baseDuration;}

                        if (this.canAssignTask(worker, task, workerOrdersMap, balanceNumber)) {
                            bestWorker = worker;
                            minTravelTime = 0; // Нет необходимости в перемещении
                            break;
                        }
                    }

                    // Если не нашли подходящего работника в той же локации, ищем среди остальных
                    if (!bestWorker) {
                        for (const [index, worker] of workers.entries()) {
                            if (balanceWorkload || assignAllTasks) {balanceNumber = workerTaskLimits?.get(worker.data.id);}
                            if (balanceByDuration) {
                                if (slotIndex % 2 === 0) {
                                    balanceNumber = baseDuration + ((index % 2 === 0) ? averageTaskDuration : 0);
                                } else {
                                    balanceNumber = baseDuration + (index % 2 !== 0 && index !== 0  ? averageTaskDuration : 0);
                                }
                            }

                            if (this.canAssignTask(worker, task, workerOrdersMap, balanceNumber)) {
                                const workerOrders = workerOrdersMap.get(worker.data.id) || [];
                                const lastTask = workerOrders[workerOrders.length - 1] || null;

                                const travelTime = lastTask
                                    ? this.calculateTravelTime(lastTask.data.location, task.data.location)
                                    : this.calculateTravelTime(worker.data.group.location, task.data.location);

                                if (travelTime < minTravelTime) {
                                    bestWorker = worker;
                                    minTravelTime = travelTime;
                                }
                            }
                        }
                    }

                    if (bestWorker) {
                        task.data = {
                            ...task.data,
                            executor: bestWorker.data,
                            status: OrderStatusEnum.destributed,
                            endTime: this.workersEndTimeLastTask.get(bestWorker.data.id) || new Date(),
                            queueNumber: (workerOrdersMap.get(bestWorker.data.id) || []).length + 1,
                        };
                        // Добавляем задачу в карту работников и заявок
                        if (!workerOrdersMap.has(bestWorker.data.id)) {
                            workerOrdersMap.set(bestWorker.data.id, []);
                        }
                        workerOrdersMap.get(bestWorker.data.id)?.push(task);

                        // Убираем назначенную задачу из общего списка задач
                        tasks.splice(tasks.indexOf(task), 1);
                    }
                }
            }
            slotIndex++;
        }

        // Оптимизация маршрутов работников
        workerOrdersMap = this.optimizeWorkerRoutes(workerOrdersMap);

        // Сохранение задач в БД
        const orders = Array.from(workerOrdersMap.values()).flat();
        const ordersDataForUpdate = orders.map(order => order.dataForMultipleUpdateExecutorIdAndStatus());
        return this.saveOrders(ordersDataForUpdate);
    }

    getNextTaskTiming(
        previousTaskEndTime: Date, // Время окончания предыдущей задачи
        nextTask: Order,         // Следующая задача
        previousTaskLocation: ILocation // Локация предыдущей задачи
    ): { startTime: Date, endTime: Date } {
        // Время на дорогу до следующей задачи
        const travelTime = this.calculateTravelTime(previousTaskLocation, nextTask.data.location);

        // Рассчитываем время прибытия
        const arrivalTime = previousTaskEndTime;
        arrivalTime.setMinutes(arrivalTime.getMinutes() + travelTime);

        // Учитываем минимальное допустимое время начала задачи
        const startTime = new Date(Math.max(arrivalTime.getTime(), new Date(nextTask.data.dateFrom).getTime()));

        // Рассчитываем время завершения задачи
        const endTime = startTime;
        endTime.setMinutes(endTime.getMinutes() + this.calculateTaskDuration(nextTask));

        return { startTime, endTime };
    }

    calculateWorkerLoad(workerTasks: Order[], task: Order): boolean {
        const taskPeriodEnd = task.data.dateTo;
        const workerLastTask = workerTasks[workerTasks.length - 1];
        const workerLastTaskPeriodEnd = workerLastTask.data.dateTo;

        if (workerLastTaskPeriodEnd.getTime() !== taskPeriodEnd.getTime()) {
            return true;
        }

        // Остаток свободного времени в минутах у работника в искомом периоде
        const restWorkerTimeInAvailableTime =
            (taskPeriodEnd.getTime() - (workerLastTask.data.endTime?.getTime() ||
                this.calculateTaskEndTime(workerTasks, task).getTime())) / (1000 * 60);

        const newTaskDuration = this.calculateTaskDuration(task);
        const travelTime = this.calculateTravelTime(workerLastTask.data.location, task.data.location);
        return travelTime + newTaskDuration <= restWorkerTimeInAvailableTime; // Ограничение на продолжительность периода
    }

    calculateTravelTime(location1: ILocation, location2: ILocation): number {
        const distance = this.calculateDistance(location1, location2);
        return distance * 2; // Время в минутах (2 мин/км)
    }

    calculateTaskDuration(task: Order): number {
        let duration = 0;

        if (task.data.dropDuration) {
            duration = task.data.dropDuration;
        } else {
            task.data.orderPrices.forEach(orderPrice => {
                duration += orderPrice.price.duration * orderPrice.quantity;
            });
        }

        // Если duration равно 0, устанавливаем его значение равным 20мин по требованию заказчика
        if (duration === 0) {
            duration = 20;
        }

        return duration * this.efficiency;
    }

    groupTasksByLocation(tasks: Order[]): Map<string, Map<string, Order[]>> {
        const groupedTasks = new Map<string, Map<string, Order[]>>();

        for (const task of tasks) {
            if(!task.data.location) {
                continue;
            }
            // Создаем ключ для внешней карты на основе dateFrom-dateTo
            const outerKey = `${task.data.dateFrom}-${task.data.dateTo}`;

            // Если внешний ключ еще не существует, создаем новую внутреннюю карту
            if (!groupedTasks.has(outerKey)) {
                groupedTasks.set(outerKey, new Map<string, Order[]>());
            }

            const innerMap = groupedTasks.get(outerKey)!;

            // Создаем ключ для внутренней карты на основе координат
            const innerKey = `${task.data.location.latitude},${task.data.location.longitude}`;

            if (!innerMap.has(innerKey)) {
                innerMap.set(innerKey, []);
            }

            innerMap.get(innerKey)!.push(task);
        }

        // Сортируем задачи в каждой внутренней группе по номеру квартиры
        for (const [outerKey, innerMap] of groupedTasks.entries()) {
            for (const [innerKey, group] of innerMap.entries()) {
                innerMap.set(
                    innerKey,
                    group.sort((a, b) => {
                        const apartmentA = this.extractApartmentNumber(a.data.addressDetails || '') ?? Infinity;
                        const apartmentB = this.extractApartmentNumber(b.data.addressDetails || '') ?? Infinity;
                        return apartmentA - apartmentB; // Сравниваем номера квартир
                    })
                );
            }

            // Выбираем первую точку как начальную для сортировки
            const [firstKey] = innerMap.keys();
            const [firstLat, firstLon] = firstKey.split(',').map(Number);
            const origin = { latitude: firstLat, longitude: firstLon };

            const sortedInnerKeys = Array.from(innerMap.keys()).sort((keyA: string, keyB: string) => {
                const [latA, lonA] = keyA.split(',').map(Number);
                const [latB, lonB] = keyB.split(',').map(Number);
                const distanceA = this.calculateDistance(origin, { latitude: latA, longitude: lonA } as ILocation);
                const distanceB = this.calculateDistance(origin, { latitude: latB, longitude: lonB } as ILocation);
                return distanceA - distanceB;
            });

            // Создаем новую отсортированную внутреннюю карту
            const sortedInnerMap = new Map<string, Order[]>();
            for (const key of sortedInnerKeys) {
                sortedInnerMap.set(key, innerMap.get(key)!);
            }

            groupedTasks.set(outerKey, sortedInnerMap);
        }

        return groupedTasks;
    }

    isSameLocation(location1: ILocation, location2: ILocation): boolean {
        return location1.latitude === location2.latitude && location1.longitude === location2.longitude;
    }

    // Функция для извлечения номера квартиры из строки адреса
    extractApartmentNumber(address: string): number | null {
        const match = address.match(/кв\.\s*(\d+)/); // Ищем "кв. <номер>"
        return match ? parseInt(match[1], 10) : null; // Возвращаем номер квартиры или null
    };

    //если у работника были назначенные задачи до того как применили автоматическое распределение заявок то в этих заявках нет endTime
    // поэтому пока вычисляю их по простому: фильтрую назначенные задачи по периоду указанному в следующей задаче (dateTo),
    // потом считаю - к время начала периода в nextTask плюс время работ в первой из lastTasks плюс время на дорогу до следующей
    // из lastTasks плюс время работ на следующей из lastTasks и т.д.
    calculateTaskEndTime(lastTasks: Order[], nextTask: Order): Date {
        // Получаем задачи, которые заканчиваются в том же временном периоде, что и начало следующей задачи
        const lastTasksInPeriod = lastTasks.filter(task =>
            new Date(task.data.dateTo).getTime() === new Date(nextTask.data.dateTo).getTime()
        );

        // Начинаем расчет времени окончания с начала периода следующей задачи
        const currentEndTime = nextTask.data.dateFrom;

        // Перебираем задачи с индексом
        lastTasksInPeriod.forEach((task, index) => {
            // Увеличиваем текущее время окончания на длительность текущей задачи
            currentEndTime.setMinutes(currentEndTime.getMinutes() + this.calculateTaskDuration(task));

            // Добавляем время на дорогу до следующей задачи, если это не последняя задача в списке
            if (index < lastTasksInPeriod.length - 1) {
                const nextTaskLocation = lastTasksInPeriod[index + 1].data.location;
                currentEndTime.setMinutes(currentEndTime.getMinutes() + this.calculateTravelTime(task.data.location, nextTaskLocation));
            } else {
                // Если это последняя задача, добавляем время до новой задачи
                currentEndTime.setMinutes(currentEndTime.getMinutes()
                    + this.calculateTravelTime(task.data.location, nextTask.data.location));
            }
        });

        return currentEndTime;
    }

    //метод принимает в себя карту работников и их заявок разбивает их заявки по отдельным слотам
    // и сортирует заявки в слотах начиная со второго чтобы заявки были в той же локации что и последняя в предыдущем слоте
    optimizeWorkerRoutes(workerOrdersMap: Map<number, Order[]>): Map<number, Order[]> {
        for (const [workerId, orders] of workerOrdersMap.entries()) {
            // Группируем заявки по временным слотам
            const ordersByTimeSlot = this.groupOrdersByTimeSlot(orders);

            let lastLocation: ILocation | null = null;
            const optimizedOrders: Order[] = [];

            // Сортируем заявки в каждом временном слоте
            for (const [timeSlot, slotOrders] of ordersByTimeSlot) {
                if (lastLocation) {
                    // Сортируем заявки по расстоянию от последней локации предыдущего слота
                    slotOrders.sort((a, b) => {
                        const distanceA = this.calculateDistance(lastLocation!, a.data.location);
                        const distanceB = this.calculateDistance(lastLocation!, b.data.location);
                        return distanceA - distanceB;
                    });
                }

                optimizedOrders.push(...slotOrders);
                lastLocation = slotOrders[slotOrders.length - 1].data.location;
            }

            // Обновляем отсортированные заявки для работника
            workerOrdersMap.set(workerId, optimizedOrders);
        }

        // Пересчитываем queueNumber после оптимизации
        this.recalculateQueueNumbers(workerOrdersMap);

        return workerOrdersMap;
    }

    private groupOrdersByTimeSlot(orders: Order[]): Map<string, Order[]> {
        const ordersByTimeSlot = new Map<string, Order[]>();
        for (const order of orders) {
            const timeSlotKey = `${order.data.dateFrom}-${order.data.dateTo}`;
            if (!ordersByTimeSlot.has(timeSlotKey)) {
                ordersByTimeSlot.set(timeSlotKey, []);
            }
            ordersByTimeSlot.get(timeSlotKey)!.push(order);
        }
        return ordersByTimeSlot;
    }

    recalculateQueueNumbers(workerOrdersMap: Map<number, Order[]>): void {
        for (const [workerId, orders] of workerOrdersMap.entries()) {
            orders.forEach((order, index) => {
                order.data.queueNumber = index + 1;
            });
        }
    }

    saveOrders(orders: IOrderDataUpdate[]): Observable<IOrder[]> {
        return this.orderService.updateOrders(orders);
    }

    cancelDistributedOrders(orders: Order[]): Observable<IOrder[]> {
        const ordersDataForUpdate = orders.filter(order => order.data.status === OrderStatusEnum.destributed)
            .map(order => order.dataForCancelDistributedOrders());
        return this.saveOrders(ordersDataForUpdate);
    }

    changeOrdersStatusToReady(orders: Order[]): Observable<IOrder[]> {
        const ordersDataForUpdate = orders.filter(order => order.data.status === OrderStatusEnum.destributed)
            .map(order => order.dataForStatusTo(OrderStatusEnum.ready));
        return this.saveOrders(ordersDataForUpdate);
    }

    // форматирование новой очереди
    setQueueAfterMultiEdit(orders: Order[]): Order[] {
        const ordersByHour = new Map<string, Order[]>();

        // Сортируем и группируем все заказы за один проход
        orders.sort((a, b) => (a.data.queueNumber ?? Infinity) - (b.data.queueNumber ?? Infinity))
            .forEach(order => {
                const hour = new Date(order.data.dateFrom).getHours().toString().padStart(2, '0');
                if (!ordersByHour.has(hour)) {
                    ordersByHour.set(hour, []);
                }
                ordersByHour.get(hour)?.push(order);
            });

        // сортируем коллекцию по ключам по возрастанию
        const sortedEntries = [...ordersByHour.entries()].sort((a, b) => a[0].localeCompare(b[0]));

        const sortedMap = new Map(sortedEntries);
        // Присваиваем сквозную нумерацию
        let globalQueueNumber = 1; // Счётчик для сквозной нумерации
        return Array.from(sortedMap.values()).flatMap(hourOrders =>
            hourOrders.map(order => {
                order.data.queueNumber = globalQueueNumber++;
                return order;
            })
        );
    }

    changeQueueManualDrop(
        changeTasks: Order[],
        allTasks: Order[],
        hoveredTaskId: number,
        isLeft: boolean,
        workerId: number
    ): IOrderDataUpdate[] {
        const filtered = allTasks
            .filter(order => !changeTasks.includes(order))
            .sort((a,b) => a.data.queueNumber! - b.data.queueNumber!); // заявки, которые НЕ перетягивались
        let orderId = filtered.findIndex(item => item.data.id === hoveredTaskId); // index заявки, до или после которой надо вставить
        const order = filtered.find(item => item.data.id === hoveredTaskId); // заявка, до или после которой надо вставить

        if(!isLeft) {
            orderId++;
        }

        const ordersBeforeChanged = filtered.slice(0, orderId).map((item, index) => {
            return {
                id: item.data.id,
                dateFrom: this.timeZoneService.setCurrentTimeZoneDateSave(item.data.dateFrom),
                dateTo: this.timeZoneService.setCurrentTimeZoneDateSave(item.data.dateTo),
                executorId: workerId,
                groupId: item?.data?.group?.id,
                queueNumber: index + 1,
                status: item.data.status
            };
        });

        const ordersForChange = changeTasks.map((item, index) => {
            return {
                id: item.data.id,
                dateFrom: order ? this.timeZoneService.setCurrentTimeZoneDateSave(order.data.dateFrom) : undefined,
                dateTo: order ? this.timeZoneService.setCurrentTimeZoneDateSave(order.data.dateTo) : undefined,
                executorId: workerId,
                groupId: item?.data.group?.id,
                queueNumber: index + orderId + 1,
                status: item.data.status === OrderStatusEnum.active ? OrderStatusEnum.destributed : item.data.status
            };
        });

        const ordersAfterChanged = filtered.slice(orderId).map((item, index) => {
            return {
                id: item.data.id,
                dateFrom: this.timeZoneService.setCurrentTimeZoneDateSave(item.data.dateFrom),
                dateTo: this.timeZoneService.setCurrentTimeZoneDateSave(item.data.dateTo),
                executorId: workerId,
                groupId: item?.data.group?.id,
                queueNumber: index + orderId + 1 + ordersForChange.length,
                status: item.data.status
            };
        });

        return [...ordersBeforeChanged, ...ordersForChange, ...ordersAfterChanged];
    }

}
